// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh-token',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    CHANGE_PASSWORD: '/auth/change-password',
    OAUTH: '/auth/oauth',
  },
  USERS: {
    BASE: '/users',
    PROFILE: '/users/profile',
    ENROLLMENTS: '/users/enrollments',
    COURSES: '/users/courses',
  },
  COURSES: {
    BASE: '/courses',
    MY_COURSES: '/courses/my-courses',
    TEACHING: '/courses/teaching',
    ENROLL: '/courses/enroll',
    CONTENT: '/courses/content',
    MODULES: '/courses/modules',
    STATS: '/courses/stats',
  },
  CONTENT: {
    BASE: '/content',
    PROGRESS: '/content/progress',
    BOOKMARK: '/content/bookmark',
    SHARE: '/content/share',
  },
  QUIZZES: {
    BASE: '/quizzes',
    START: '/quizzes/start',
    SUBMIT: '/quizzes/submit',
    RESULTS: '/quizzes/results',
    SUBMISSIONS: '/quizzes/submissions',
  },
  DISCUSSION: {
    BASE: '/discussion',
    VOTE: '/discussion/vote',
    COMMENTS: '/discussion/comments',
    PIN: '/discussion/pin',
    LOCK: '/discussion/lock',
  },
  UNIVERSITIES: {
    BASE: '/universities',
    DEPARTMENTS: '/universities/departments',
    STATS: '/universities/stats',
    SETTINGS: '/universities/settings',
  },
  GRADES: {
    BASE: '/grades',
    CATEGORIES: '/grades/categories',
    ITEMS: '/grades/items',
    FINAL: '/grades/final',
    RUBRICS: '/grades/rubrics',
  },
  NOTIFICATIONS: {
    BASE: '/notifications',
    PREFERENCES: '/notifications/preferences',
  },
  ANALYTICS: {
    BASE: '/analytics',
    REPORTS: '/analytics/reports',
  },
  SEARCH: {
    BASE: '/search',
    COURSES: '/search/courses',
    USERS: '/search/users',
    CONTENT: '/search/content',
  },
  UPLOAD: {
    BASE: '/upload',
    MULTIPLE: '/upload/multiple',
  },
};

// User Roles
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  UNIVERSITY_ADMIN: 'university_admin',
  // Dean of a faculty. Scoped to the departments assigned to them, not the whole
  // institution — see backend utils/facultyScope.js.
  HEAD_OF_FACULTY: 'head_of_faculty',
  TEACHER: 'teacher',
  STUDENT: 'student',
};

// Role Labels
export const ROLE_LABELS = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Admin',
  [USER_ROLES.UNIVERSITY_ADMIN]: 'University Admin',
  [USER_ROLES.HEAD_OF_FACULTY]: 'Head of Faculty',
  [USER_ROLES.TEACHER]: 'Teacher',
  [USER_ROLES.STUDENT]: 'Student',
};

// Role Permissions
export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: [
    'manage_system',
    'manage_universities',
    'manage_all_users',
    'view_all_courses',
    'manage_system_settings',
    'view_analytics',
    'manage_billing',
  ],
  [USER_ROLES.UNIVERSITY_ADMIN]: [
    'manage_university',
    'manage_departments',
    'manage_faculty',
    'manage_students',
    'view_university_courses',
    'manage_university_settings',
    'view_university_analytics',
  ],
  [USER_ROLES.TEACHER]: [
    'manage_courses',
    'create_content',
    'grade_assignments',
    'create_quizzes',
    'manage_discussions',
    'view_students',
    'view_course_analytics',
  ],
  [USER_ROLES.STUDENT]: [
    'enroll_courses',
    'view_content',
    'submit_assignments',
    'take_quizzes',
    'participate_discussions',
    'view_grades',
  ],
};

// Content Types
export const CONTENT_TYPES = {
  ANNOUNCEMENT: 'announcement',
  LECTURE: 'lecture',
  ASSIGNMENT: 'assignment',
  READING: 'reading',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  PRESENTATION: 'presentation',
  LINK: 'link',
  FILE: 'file',
};

// Content Type Labels
export const CONTENT_TYPE_LABELS = {
  [CONTENT_TYPES.ANNOUNCEMENT]: 'Announcement',
  [CONTENT_TYPES.LECTURE]: 'Lecture',
  [CONTENT_TYPES.ASSIGNMENT]: 'Assignment',
  [CONTENT_TYPES.READING]: 'Reading',
  [CONTENT_TYPES.VIDEO]: 'Video',
  [CONTENT_TYPES.AUDIO]: 'Audio',
  [CONTENT_TYPES.DOCUMENT]: 'Document',
  [CONTENT_TYPES.PRESENTATION]: 'Presentation',
  [CONTENT_TYPES.LINK]: 'Link',
  [CONTENT_TYPES.FILE]: 'File',
};

// Access Types
export const ACCESS_TYPES = {
  PUBLIC: 'public',
  ENROLLED: 'enrolled',
  INSTRUCTOR_ONLY: 'instructor_only',
  ADMIN_ONLY: 'admin_only',
};

// Quiz Question Types
export const QUESTION_TYPES = {
  MULTIPLE_CHOICE: 'multiple_choice',
  TRUE_FALSE: 'true_false',
  SHORT_ANSWER: 'short_answer',
  ESSAY: 'essay',
  MATCHING: 'matching',
  FILL_BLANK: 'fill_blank',
};

// Quiz Grading Methods
export const GRADING_METHODS = {
  HIGHEST: 'highest',
  LATEST: 'latest',
  AVERAGE: 'average',
};

// Enrollment Status
export const ENROLLMENT_STATUS = {
  ENROLLED: 'enrolled',
  DROPPED: 'dropped',
  COMPLETED: 'completed',
  WITHDRAWN: 'withdrawn',
  PENDING: 'pending',
};

// Course Semesters
export const SEMESTERS = {
  SPRING: 'spring',
  SUMMER: 'summer',
  FALL: 'fall',
  WINTER: 'winter',
};

// Submission Status
export const SUBMISSION_STATUS = {
  IN_PROGRESS: 'in_progress',
  SUBMITTED: 'submitted',
  GRADED: 'graded',
  REVIEWED: 'reviewed',
};

// Notification Types
export const NOTIFICATION_TYPES = {
  COURSE_ANNOUNCEMENT: 'course_announcement',
  ASSIGNMENT_POSTED: 'assignment_posted',
  ASSIGNMENT_GRADED: 'assignment_graded',
  QUIZ_POSTED: 'quiz_posted',
  QUIZ_GRADED: 'quiz_graded',
  DISCUSSION_REPLY: 'discussion_reply',
  MESSAGE_RECEIVED: 'message_received',
  MENTION: 'mention',
  ENROLLMENT_CONFIRMED: 'enrollment_confirmed',
  GRADE_POSTED: 'grade_posted',
  COURSE_START: 'course_start',
  COURSE_END: 'course_end',
  DEADLINE_REMINDER: 'deadline_reminder',
  SYSTEM_ANNOUNCEMENT: 'system_announcement',
};

// Notification Priorities
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
};

// Subscription Plans
export const SUBSCRIPTION_PLANS = {
  FREE: 'free',
  BASIC: 'basic',
  PREMIUM: 'premium',
  ENTERPRISE: 'enterprise',
};

// File Categories
export const FILE_CATEGORIES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  ARCHIVE: 'archive',
  ALL: 'all',
};

// File Size Limits (in bytes)
export const FILE_SIZE_LIMITS = {
  [FILE_CATEGORIES.IMAGE]: 5 * 1024 * 1024, // 5MB
  [FILE_CATEGORIES.VIDEO]: 500 * 1024 * 1024, // 500MB
  [FILE_CATEGORIES.AUDIO]: 100 * 1024 * 1024, // 100MB
  [FILE_CATEGORIES.DOCUMENT]: 50 * 1024 * 1024, // 50MB
  [FILE_CATEGORIES.ARCHIVE]: 200 * 1024 * 1024, // 200MB
  [FILE_CATEGORIES.ALL]: 100 * 1024 * 1024, // 100MB
};

// Allowed File Types
export const ALLOWED_FILE_TYPES = {
  [FILE_CATEGORIES.IMAGE]: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  [FILE_CATEGORIES.VIDEO]: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
  [FILE_CATEGORIES.AUDIO]: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4'],
  [FILE_CATEGORIES.DOCUMENT]: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
  ],
  [FILE_CATEGORIES.ARCHIVE]: ['application/zip', 'application/x-zip-compressed'],
};

// Grade Letters
export const GRADE_LETTERS = {
  A_PLUS: 'A+',
  A: 'A',
  A_MINUS: 'A-',
  B_PLUS: 'B+',
  B: 'B',
  B_MINUS: 'B-',
  C_PLUS: 'C+',
  C: 'C',
  C_MINUS: 'C-',
  D_PLUS: 'D+',
  D: 'D',
  D_MINUS: 'D-',
  F: 'F',
  I: 'I',
  W: 'W',
};

// GPA Values
export const GPA_VALUES = {
  [GRADE_LETTERS.A_PLUS]: 4.0,
  [GRADE_LETTERS.A]: 4.0,
  [GRADE_LETTERS.A_MINUS]: 3.7,
  [GRADE_LETTERS.B_PLUS]: 3.3,
  [GRADE_LETTERS.B]: 3.0,
  [GRADE_LETTERS.B_MINUS]: 2.7,
  [GRADE_LETTERS.C_PLUS]: 2.3,
  [GRADE_LETTERS.C]: 2.0,
  [GRADE_LETTERS.C_MINUS]: 1.7,
  [GRADE_LETTERS.D_PLUS]: 1.3,
  [GRADE_LETTERS.D]: 1.0,
  [GRADE_LETTERS.D_MINUS]: 0.7,
  [GRADE_LETTERS.F]: 0.0,
  [GRADE_LETTERS.I]: 0.0,
  [GRADE_LETTERS.W]: 0.0,
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  DISPLAY_WITH_TIME: 'MMM d, yyyy h:mm a',
  ISO: 'yyyy-MM-dd',
  ISO_WITH_TIME: 'yyyy-MM-dd HH:mm:ss',
  TIME: 'h:mm a',
  TIME_24H: 'HH:mm',
  MONTH_DAY: 'MMM d',
  MONTH_YEAR: 'MMMM yyyy',
};

// Pagination
export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Chart Colors
export const CHART_COLORS = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#9c27b0',
  '#d32f2f',
  '#0288d1',
  '#7b1fa2',
  '#c62828',
  '#2c3e50',
  '#e67e22',
];

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  THEME: 'theme',
  LANGUAGE: 'language',
  SIDEBAR_OPEN: 'sidebarOpen',
};

// Route Paths
export const ROUTES = {
  // Public
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_EMAIL: '/verify-email',
  
  // Protected
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  
  // Courses
  COURSES: '/courses',
  COURSE_DETAIL: '/courses/:id',
  CREATE_COURSE: '/courses/create',
  EDIT_COURSE: '/courses/:id/edit',
  MY_COURSES: '/my-courses',
  TEACHING: '/teaching',
  
  // Content
  CONTENT: '/content/:id',
  CREATE_CONTENT: '/content/create',
  EDIT_CONTENT: '/content/:id/edit',
  
  // Quizzes
  QUIZZES: '/quizzes',
  QUIZ_DETAIL: '/quizzes/:id',
  TAKE_QUIZ: '/quizzes/:id/take/:submissionId',
  QUIZ_RESULTS: '/quizzes/:id/results',
  CREATE_QUIZ: '/quizzes/create',
  
  // Discussions
  DISCUSSIONS: '/discussions',
  DISCUSSION_DETAIL: '/discussions/:id',
  
  // Grades
  GRADES: '/grades',
  GRADEBOOK: '/gradebook',
  
  // Admin
  USERS: '/users',
  UNIVERSITIES: '/universities',
  DEPARTMENTS: '/departments',
  ANALYTICS: '/analytics',
  
  // Other
  CALENDAR: '/calendar',
  BOOKMARKS: '/bookmarks',
  NOTIFICATIONS: '/notifications',
  HELP: '/help',
  ABOUT: '/about',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Unable to connect to server. Please check your internet connection.',
  UNAUTHORIZED: 'Your session has expired. Please login again.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'An unexpected server error occurred. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PASSWORD: 'Password must be at least 8 characters and contain uppercase, lowercase, number, and special character.',
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match.',
  INVALID_FILE_TYPE: 'File type is not allowed.',
  FILE_TOO_LARGE: 'File size exceeds the maximum limit.',
  UPLOAD_FAILED: 'File upload failed. Please try again.',
  ENROLLMENT_FAILED: 'Failed to enroll in course. Please check your enrollment code.',
  QUIZ_SUBMISSION_FAILED: 'Failed to submit quiz. Please try again.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Successfully logged in!',
  LOGOUT: 'Successfully logged out!',
  REGISTER: 'Account created successfully! Please verify your email.',
  PASSWORD_RESET: 'Password reset successfully!',
  PASSWORD_CHANGED: 'Password changed successfully!',
  EMAIL_VERIFIED: 'Email verified successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  COURSE_CREATED: 'Course created successfully!',
  COURSE_UPDATED: 'Course updated successfully!',
  COURSE_DELETED: 'Course deleted successfully!',
  CONTENT_CREATED: 'Content created successfully!',
  CONTENT_UPDATED: 'Content updated successfully!',
  CONTENT_DELETED: 'Content deleted successfully!',
  ENROLLMENT_SUCCESS: 'Successfully enrolled in course!',
  ENROLLMENT_CANCELLED: 'Successfully dropped course!',
  QUIZ_CREATED: 'Quiz created successfully!',
  QUIZ_UPDATED: 'Quiz updated successfully!',
  QUIZ_DELETED: 'Quiz deleted successfully!',
  QUIZ_SUBMITTED: 'Quiz submitted successfully!',
  QUIZ_PUBLISHED: 'Quiz published successfully!',
  DISCUSSION_CREATED: 'Discussion post created successfully!',
  DISCUSSION_UPDATED: 'Discussion post updated successfully!',
  DISCUSSION_DELETED: 'Discussion post deleted successfully!',
  COMMENT_ADDED: 'Comment added successfully!',
  VOTE_RECORDED: 'Vote recorded successfully!',
  FILE_UPLOADED: 'File uploaded successfully!',
  UNIVERSITY_CREATED: 'University created successfully!',
  UNIVERSITY_UPDATED: 'University updated successfully!',
  DEPARTMENT_CREATED: 'Department created successfully!',
  USER_CREATED: 'User created successfully!',
};

// Validation Patterns
export const PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  PHONE: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}$/,
  URL: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
  COURSE_CODE: /^[A-Z0-9-]+$/,
  DEPARTMENT_CODE: /^[A-Z0-9]+$/,
  STUDENT_ID: /^[A-Z0-9-]+$/,
  EMPLOYEE_ID: /^[A-Z0-9-]+$/,
  ENROLLMENT_CODE: /^[A-Z0-9]+$/,
  SLUG: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  HEX_COLOR: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
};

// Default Values
export const DEFAULTS = {
  COURSE_MAX_STUDENTS: 50,
  QUIZ_TIME_LIMIT: 30,
  QUIZ_MAX_ATTEMPTS: 1,
  PAGE_SIZE: 20,
  NOTIFICATION_REFRESH_INTERVAL: 30000, // 30 seconds
  SESSION_TIMEOUT: 3600000, // 1 hour
  UPLOAD_TIMEOUT: 300000, // 5 minutes
  DEBOUNCE_DELAY: 300, // 300ms
  AUTO_SAVE_DELAY: 2000, // 2 seconds
};

// Theme Options
export const THEME_OPTIONS = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Language Options
export const LANGUAGE_OPTIONS = {
  EN: 'en',
  ES: 'es',
  FR: 'fr',
  DE: 'de',
  ZH: 'zh',
  JA: 'ja',
  AR: 'ar',
};

// Language Labels
export const LANGUAGE_LABELS = {
  [LANGUAGE_OPTIONS.EN]: 'English',
  [LANGUAGE_OPTIONS.ES]: 'Español',
  [LANGUAGE_OPTIONS.FR]: 'Français',
  [LANGUAGE_OPTIONS.DE]: 'Deutsch',
  [LANGUAGE_OPTIONS.ZH]: '中文',
  [LANGUAGE_OPTIONS.JA]: '日本語',
  [LANGUAGE_OPTIONS.AR]: 'العربية',
};

// Sort Options
export const SORT_OPTIONS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  NAME_ASC: 'name_asc',
  NAME_DESC: 'name_desc',
  POPULAR: 'popular',
  RELEVANCE: 'relevance',
};

// Time Periods
export const TIME_PERIODS = {
  TODAY: 'today',
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  THIS_SEMESTER: 'this_semester',
  THIS_YEAR: 'this_year',
  ALL_TIME: 'all_time',
};

// Event Types
export const EVENT_TYPES = {
  COURSE_START: 'course_start',
  COURSE_END: 'course_end',
  ASSIGNMENT_DUE: 'assignment_due',
  QUIZ_DUE: 'quiz_due',
  EXAM: 'exam',
  OFFICE_HOURS: 'office_hours',
  MEETING: 'meeting',
  HOLIDAY: 'holiday',
};
